import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, Mail, ArrowRight, LogIn, Hammer } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, LOGO_URL } from "@/lib/api";

const STORAGE_KEY = "cursus_construction_unlocked_at";
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export function isConstructionUnlocked() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return false;
        const ts = parseInt(raw, 10);
        if (Number.isNaN(ts)) return false;
        return Date.now() - ts < THIRTY_DAYS_MS;
    } catch {
        return false;
    }
}

function markUnlocked() {
    try {
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
        /* ignore */
    }
}

export default function ConstructionPage({ onUnlock }) {
    const [email, setEmail] = useState("");
    const [pwd, setPwd] = useState("");
    const [submittingEmail, setSubmittingEmail] = useState(false);
    const [submittingPwd, setSubmittingPwd] = useState(false);
    const [pwdError, setPwdError] = useState("");

    useEffect(() => {
        document.title = "Cursus Physio · Notre Institut prend forme";
    }, []);

    const submitEmail = async (e) => {
        e.preventDefault();
        if (!email.includes("@")) {
            toast.error("Merci de saisir un email valide.");
            return;
        }
        setSubmittingEmail(true);
        try {
            const { data } = await api.post("/construction/notify", { email: email.trim().toLowerCase(), source: "construction_page" });
            if (data.status === "already_registered") {
                toast.success("Vous êtes déjà inscrit. Merci !");
            } else {
                toast.success("Merci ! Vous serez averti dès l'ouverture.");
            }
            setEmail("");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmittingEmail(false);
        }
    };

    const submitPwd = async (e) => {
        e.preventDefault();
        setPwdError("");
        setSubmittingPwd(true);
        try {
            await api.post("/construction/unlock", { password: pwd });
            markUnlocked();
            toast.success("Accès partenaire autorisé.");
            if (onUnlock) onUnlock();
        } catch (err) {
            setPwdError("Mot de passe invalide.");
        } finally {
            setSubmittingPwd(false);
        }
    };

    return (
        <div
            className="relative min-h-screen overflow-hidden bg-[#33333A] text-white"
            data-testid="construction-page"
        >
            {/* Filigrane image — overlay 10% */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-[0.10]"
                style={{
                    backgroundImage:
                        'url("https://static.prod-images.emergentagent.com/jobs/6c3dbc7e-e3b1-4c5d-b777-4fea74d38e17/images/e7dd10aa4b39ecda9f3db23f3fc26174bbb42b3193aeacf77142ca65e517a13c.jpeg")',
                }}
            />
            {/* Orange glow */}
            <div aria-hidden className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-brand-orange/15 blur-[140px]" />

            <div className="relative mx-auto flex min-h-screen max-w-3xl flex-col px-6 py-10 sm:py-14">
                {/* Top bar : logo + pro login */}
                <div className="flex items-center justify-between gap-3">
                    <img src={LOGO_URL} alt="Cursus Physio" className="h-12 w-auto flex-shrink-0 sm:h-16" />
                    <a
                        href="/login"
                        data-testid="construction-pro-login"
                        className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border border-white/30 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:border-white sm:px-4 sm:text-[11px]"
                    >
                        <LogIn className="h-3.5 w-3.5" /> Espace pro
                    </a>
                </div>

                {/* Hero */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-14 sm:mt-20"
                >
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-orange/40 bg-brand-orange/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-brand-orange">
                        <Hammer className="h-3 w-3" /> En construction
                    </span>
                    <h1 className="mt-6 font-display text-5xl font-normal leading-[0.95] tracking-tightest sm:text-6xl lg:text-7xl">
                        Notre Institut <em className="italic text-white/65">prend forme.</em>
                    </h1>
                    <p className="mt-5 max-w-xl text-base text-white/70 sm:text-lg">
                        <strong className="text-white">Cursus Physio</strong> · L'Institut de la Prévention Active arrive
                        bientôt. Formation continue certifiée Qualiopi pour kinésithérapeutes engagés.
                    </p>
                </motion.div>

                {/* Notify email */}
                <motion.form
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.2 }}
                    onSubmit={submitEmail}
                    className="mt-12 rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-7"
                    data-testid="construction-notify-form"
                >
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-orange">
                        <Mail className="h-3.5 w-3.5" /> Être averti de l'ouverture officielle
                    </div>
                    <p className="mt-2 text-sm text-white/65">
                        Laissez votre email — nous vous préviendrons dès que le site sera en ligne.
                    </p>
                    <div className="mt-4 flex flex-col gap-2.5 sm:flex-row">
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="votre@email.fr"
                            data-testid="construction-notify-email"
                            className="flex-1 rounded-xl border border-white/15 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-white/35 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/30"
                        />
                        <button
                            type="submit"
                            disabled={submittingEmail}
                            data-testid="construction-notify-submit"
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-orange px-5 py-3 text-sm font-semibold text-white transition-all hover:bg-[#d9772c] disabled:opacity-60"
                        >
                            {submittingEmail ? "Envoi…" : "M'inscrire"} <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                </motion.form>

                {/* Separator */}
                <div className="mt-12 flex items-center gap-4">
                    <span className="h-px flex-1 bg-white/10" />
                    <span className="text-[10px] font-semibold uppercase tracking-[0.32em] text-white/40">
                        Accès partenaire
                    </span>
                    <span className="h-px flex-1 bg-white/10" />
                </div>

                {/* Password */}
                <motion.form
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.3 }}
                    onSubmit={submitPwd}
                    className="mt-8 rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-7"
                    data-testid="construction-unlock-form"
                >
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-orange">
                        <Lock className="h-3.5 w-3.5" /> Mot de passe d'accès anticipé
                    </div>
                    <p className="mt-2 text-sm text-white/65">
                        Vous avez reçu un code d'accès partenaire ? Saisissez-le pour prévisualiser le site.
                    </p>
                    <div className="mt-4 flex flex-col gap-2.5 sm:flex-row">
                        <input
                            type="password"
                            required
                            value={pwd}
                            onChange={(e) => { setPwd(e.target.value); if (pwdError) setPwdError(""); }}
                            placeholder="Mot de passe"
                            data-testid="construction-unlock-password"
                            className="flex-1 rounded-xl border border-white/15 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-white/35 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/30"
                        />
                        <button
                            type="submit"
                            disabled={submittingPwd || !pwd}
                            data-testid="construction-unlock-submit"
                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/30 bg-transparent px-5 py-3 text-sm font-semibold text-white transition-colors hover:border-white hover:bg-white/5 disabled:opacity-60"
                        >
                            {submittingPwd ? "Vérification…" : "Accéder"} <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                    {pwdError && (
                        <p className="mt-3 text-xs text-red-300" data-testid="construction-unlock-error">
                            {pwdError}
                        </p>
                    )}
                </motion.form>

                {/* Footer */}
                <div className="mt-auto pt-14 text-xs text-white/40">
                    © 2026 Cursus Physio · <a href="mailto:contact@cursus-physio.fr" className="text-white/60 hover:text-white">contact@cursus-physio.fr</a>
                </div>
            </div>
        </div>
    );
}
